import { Controller } from "@hotwired/stimulus"

// Handles responsive behaviors and mobile interactions
export default class extends Controller {
  static targets = ["menu", "overlay", "toggle", "content"]
  static values = { 
    breakpoint: { type: Number, default: 768 },
    open: { type: Boolean, default: false }
  }
  static classes = ["active", "hidden"]
  
  connect() {
    // Set initial state
    this.setupResponsiveListeners()
    this.checkViewport()
    
    // Handle escape key
    this.handleEscape = this.handleEscape.bind(this)
    document.addEventListener('keydown', this.handleEscape)
  }
  
  setupResponsiveListeners() {
    // Use ResizeObserver for better performance than window resize
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.checkViewport()
      })
      this.resizeObserver.observe(document.body)
    } else {
      // Fallback to resize event with debouncing
      let resizeTimer
      this.handleResize = () => {
        clearTimeout(resizeTimer)
        resizeTimer = setTimeout(() => this.checkViewport(), 100)
      }
      window.addEventListener('resize', this.handleResize)
    }
  }
  
  checkViewport() {
    const isMobile = window.innerWidth < this.breakpointValue
    
    // Auto-close menu when switching to desktop
    if (!isMobile && this.openValue) {
      this.close()
    }
    
    // Update body class for CSS hooks
    document.body.classList.toggle('is-mobile', isMobile)
    document.body.classList.toggle('is-desktop', !isMobile)
  }
  
  // Toggle mobile menu
  toggle(event) {
    event?.preventDefault()
    this.openValue = !this.openValue
    this.updateMenu()
  }
  
  open(event) {
    event?.preventDefault()
    this.openValue = true
    this.updateMenu()
  }
  
  close(event) {
    event?.preventDefault()
    this.openValue = false
    this.updateMenu()
  }
  
  updateMenu() {
    // Update menu visibility
    if (this.hasMenuTarget) {
      this.menuTarget.classList.toggle(this.activeClass, this.openValue)
      this.menuTarget.classList.toggle(this.hiddenClass, !this.openValue)
      
      // Set ARIA attributes
      this.menuTarget.setAttribute('aria-hidden', !this.openValue)
    }
    
    // Update overlay if present
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.toggle(this.activeClass, this.openValue)
      this.overlayTarget.classList.toggle(this.hiddenClass, !this.openValue)
    }
    
    // Update toggle button if present
    if (this.hasToggleTarget) {
      this.toggleTarget.setAttribute('aria-expanded', this.openValue)
      this.toggleTarget.classList.toggle('is-active', this.openValue)
    }
    
    // Prevent body scroll when menu is open
    document.body.classList.toggle('overflow-hidden', this.openValue)
    
    // Focus management
    if (this.openValue && this.hasMenuTarget) {
      // Focus first focusable element in menu
      const firstFocusable = this.menuTarget.querySelector(
        'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (firstFocusable) {
        setTimeout(() => firstFocusable.focus(), 100)
      }
    } else if (this.hasToggleTarget) {
      // Return focus to toggle button
      this.toggleTarget.focus()
    }
  }
  
  // Close on escape key
  handleEscape(event) {
    if (event.key === 'Escape' && this.openValue) {
      this.close()
    }
  }
  
  // Close when clicking overlay
  clickOutside(event) {
    if (this.openValue && !this.menuTarget.contains(event.target)) {
      this.close()
    }
  }
  
  // Prevent close when clicking inside menu
  clickInside(event) {
    event.stopPropagation()
  }
  
  // Handle swipe gestures for mobile
  handleSwipe(event) {
    if (!('TouchEvent' in window)) return
    
    const touch = event.touches[0]
    
    if (event.type === 'touchstart') {
      this.touchStartX = touch.clientX
      this.touchStartY = touch.clientY
    } else if (event.type === 'touchend') {
      const touchEndX = event.changedTouches[0].clientX
      const touchEndY = event.changedTouches[0].clientY
      
      const deltaX = touchEndX - this.touchStartX
      const deltaY = Math.abs(touchEndY - this.touchStartY)
      
      // Detect horizontal swipe (more horizontal than vertical movement)
      if (Math.abs(deltaX) > 50 && deltaY < 100) {
        if (deltaX > 0 && !this.openValue) {
          // Swipe right - open menu
          this.open()
        } else if (deltaX < 0 && this.openValue) {
          // Swipe left - close menu
          this.close()
        }
      }
    }
  }
  
  // Utility method to check if device is mobile
  isMobile() {
    return window.innerWidth < this.breakpointValue
  }
  
  // Utility method to check if device supports touch
  isTouch() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0
  }
  
  disconnect() {
    // Clean up event listeners
    document.removeEventListener('keydown', this.handleEscape)
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    } else if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize)
    }
    
    // Reset body classes
    document.body.classList.remove('overflow-hidden', 'is-mobile', 'is-desktop')
  }
}
